import { useEffect, useId, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Download, Trash2, UploadCloud } from "lucide-react"
import { useEmployeeDocuments } from "@/modules/employee/employee/hooks/useEmployeeDocuments"
import type { EmployeeDocumentItemDto } from "@/modules/employee/employee/dto/employeeDocuments"
import { formatBytes, formatUtcDate } from "@/lib/helpers"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modules/shared/ui/components/alert-dialog"

const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"]
const INVALID_TYPE_MESSAGE = "Only PDF, DOC, and DOCX files are accepted."

interface DocumentsListSectionProps {
  title?: string
  description?: string
  emptyMessage: string
  documents: EmployeeDocumentItemDto[]
  onDownload: (documentId: string) => void
  onDelete?: (document: EmployeeDocumentItemDto) => void
  deletingDocumentIds?: Set<string>
}

function DocumentsListSection({
  title,
  description,
  emptyMessage,
  documents,
  onDownload,
  onDelete,
  deletingDocumentIds,
}: DocumentsListSectionProps) {
  return (
    <Card className="border border-border shadow-sm">
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent>
        {documents.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-muted p-4 text-sm text-muted-foreground">
            {emptyMessage}
          </p>
        ) : (
          <div className="space-y-3">
            {documents.map((document) => {
              const deleting = deletingDocumentIds?.has(document.id) ?? false

              return (
                <div
                  key={document.id}
                  className="flex flex-col gap-3 rounded-md border border-border p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">
                      {document.fileName}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {document.source} • {formatBytes(document.size)} •{" "}
                      {formatUtcDate(document.createdAt)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDownload(document.id)}
                      aria-label={`View ${document.fileName}`}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      View
                    </Button>

                    {onDelete && document.canDelete && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDelete(document)}
                        disabled={deleting}
                        aria-label={`Delete ${document.fileName}`}
                      >
                        {deleting ? (
                          <>
                            <Spinner className="mr-2 h-4 w-4" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card className="border border-border shadow-sm">
        <CardHeader>
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>

      <Card className="border border-border shadow-sm">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

export default function EmployeeDocumentsPage() {
  const navigate = useNavigate()
  const fileInputId = useId()
  const [validationError, setValidationError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [documentPendingDelete, setDocumentPendingDelete] =
    useState<EmployeeDocumentItemDto | null>(null)

  const {
    onboardingDocuments,
    myDocuments,
    isInitialLoading,
    isUploading,
    uploadProgressPercent,
    deletingDocumentIds,
    inactiveEmployeeBlocked,
    employeeNotFound,
    shouldRelogin,
    genericError,
    uploadError,
    loadDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
  } = useEmployeeDocuments()

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  useEffect(() => {
    if (shouldRelogin) {
      navigate("/login", { replace: true })
    }
  }, [navigate, shouldRelogin])

  const isExtensionAllowed = useMemo(
    () => (fileName: string) => {
      const lower = fileName.toLowerCase()
      return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
    },
    [],
  )

  const handleSelectedFile = async (file: File | null) => {
    if (!file) {
      return
    }

    if (!isExtensionAllowed(file.name)) {
      setValidationError(INVALID_TYPE_MESSAGE)
      return
    }

    setValidationError(null)
    setSelectedFile(file)
  }

  const onUploadConfirmed = async () => {
    if (!selectedFile) {
      return
    }

    const didUpload = await uploadDocument(selectedFile)
    if (didUpload) {
      setSelectedFile(null)
    }
  }

  const onDeleteConfirmed = async () => {
    if (!documentPendingDelete) {
      return
    }

    await deleteDocument(documentPendingDelete)
    setDocumentPendingDelete(null)
  }

  if (inactiveEmployeeBlocked) {
    return (
      <div className="min-h-full bg-muted px-4 py-8 md:px-8">
        <div className="mx-auto w-full max-w-3xl">
          <Card className="border border-border shadow-sm">
            <CardContent className="py-10 text-center">
              <p className="text-base text-foreground">
                Your profile is currently being reviewed. Once approved by your
                administrator, your documents will be available here.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-muted px-4 py-8 md:px-8">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Document Management
          </h1>
          <p className="text-sm text-muted-foreground">
            View onboarding documents and manage your uploaded documents.
          </p>
        </div>

        {isInitialLoading && <LoadingSkeleton />}

        {!isInitialLoading && employeeNotFound && (
          <Card className="border border-border shadow-sm">
            <CardContent className="flex flex-col items-center gap-4 py-10">
              <p className="text-sm text-muted-foreground">
                We could not find your employee profile. Please contact support.
              </p>
              <Button onClick={loadDocuments}>Retry</Button>
            </CardContent>
          </Card>
        )}

        {!isInitialLoading && !employeeNotFound && genericError && (
          <Card className="border border-border shadow-sm">
            <CardContent className="flex flex-col items-center gap-4 py-10">
              <p className="text-sm text-muted-foreground">
                {genericError || "Something went wrong. Please try again."}
              </p>
              <Button onClick={loadDocuments}>Retry</Button>
            </CardContent>
          </Card>
        )}

        {!isInitialLoading && !employeeNotFound && !genericError && (
          <>
            <DocumentsListSection
              title="Onboarding Documents"
              description="Documents provided during onboarding."
              emptyMessage="No onboarding documents found."
              documents={onboardingDocuments}
              onDownload={downloadDocument}
            />

            <Card className="border border-border shadow-sm">
              <CardHeader>
                <CardTitle>My Documents</CardTitle>
                <CardDescription>
                  Upload and manage your own documents.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <label
                  htmlFor={fileInputId}
                  className="flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted px-4 py-8 text-center transition hover:border-border/70 hover:bg-muted"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    void handleSelectedFile(
                      event.dataTransfer.files?.[0] ?? null,
                    )
                  }}
                >
                  <UploadCloud className="mb-2 h-6 w-6 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">
                    Drag and drop a file here, or click to browse
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Accepted: PDF, DOC, DOCX
                  </p>
                </label>
                <input
                  id={fileInputId}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(event) => {
                    void handleSelectedFile(event.target.files?.[0] ?? null)
                    event.currentTarget.value = ""
                  }}
                />

                {selectedFile && (
                  <div className="rounded-md border border-border bg-card p-3 text-sm text-foreground">
                    <p className="font-medium text-foreground">
                      Selected file: {selectedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Size: {formatBytes(selectedFile.size)}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => {
                      void onUploadConfirmed()
                    }}
                    disabled={!selectedFile || isUploading}
                  >
                    Upload Selected File
                  </Button>
                </div>

                {isUploading && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner className="h-4 w-4" /> Uploading document...
                    {uploadProgressPercent > 0
                      ? ` ${uploadProgressPercent}%`
                      : ""}
                  </p>
                )}

                {validationError && (
                  <p className="text-sm text-red-600">{validationError}</p>
                )}

                {!validationError && uploadError && (
                  <p className="text-sm text-red-600">{uploadError}</p>
                )}

                <DocumentsListSection
                  emptyMessage="You haven't uploaded any documents yet."
                  documents={myDocuments}
                  onDownload={downloadDocument}
                  onDelete={(document) => setDocumentPendingDelete(document)}
                  deletingDocumentIds={deletingDocumentIds}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <AlertDialog
        open={!!documentPendingDelete}
        onOpenChange={(open) => {
          if (!open) {
            setDocumentPendingDelete(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete [{documentPendingDelete?.fileName}
              ]? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void onDeleteConfirmed()
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
